import type { BlogPost, BlogSection } from './blog/posts';
import { getCouncilConfig } from './councils';
import { buildPlanningApplicationPath as buildCouncilApplicationPath } from './planningApplicationUrl';

export interface GeneratedPlanningArticle {
  slug: string;
  reference_no: string;
  sourceCouncil?: string;
  title: string;
  excerpt: string;
  markdown: string;
  author?: string;
  location?: string;
  publishedAt: string;
  updatedAt?: string;
  readingTime?: number;
  tags?: string[];
  coverLabel?: string;
  applicationPath?: string;
  councilLink?: string | null;
  seo?: {
    seoTitle?: string;
    metaDescription?: string;
    keywordTargets?: string[];
    suggestedSlug?: string;
  };
}

const APPLICATIONS_BASE_URL =
  process.env.NEXT_PUBLIC_APPLICATIONS_API_URL ||
  process.env.APPLICATIONS_API_URL ||
  'https://api.city-scope.co.uk/applications';
const ARTICLE_FETCH_TIMEOUT_MS = 8000;
type FetchJsonOptions = {
  timeoutMs?: number;
  throwOnUnavailable?: boolean;
};

export function buildPlanningArticleRequestHeaders(isServer = typeof window === 'undefined') {
  return {
    Accept: 'application/json',
    ...(isServer
      ? {
          // Reuse the existing server-rendered detail identity recognised by
          // Nginx. A private article-only value falls through to the generic
          // Node limiter and can turn ordinary ISR reads into HTTP 429s.
          'User-Agent': 'Mozilla/5.0 CityScopeArticleReader/1.0',
          'X-CityScope-Interactive': 'ssr-detail',
        }
      : {}),
  };
}

function getApiRoot() {
  return APPLICATIONS_BASE_URL.replace(/\/applications\/?$/i, '').replace(/\/$/, '');
}

function normalizeRef(value: unknown): string {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

function cleanLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function buildPlanningApplicationPath(reference: string, council?: string) {
  return buildCouncilApplicationPath(reference, council);
}

export function extractPlanningReferenceFromLegacyArticleSlug(slug: string) {
  const match = String(slug || '').match(/(?:^|-)(\d{2})-(\d{3,6})-([a-z][a-z0-9]{1,11})$/i);
  if (!match) return null;
  return `${match[1]}/${match[2]}/${match[3].toUpperCase()}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(Boolean).map(String) : [];
}

async function fetchJson<T>(url: string, options: FetchJsonOptions = {}): Promise<T | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? ARTICLE_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: buildPlanningArticleRequestHeaders(),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as T;
  } catch (error) {
    console.warn('[planningArticles] Failed to fetch planning articles', url, error);
    if (options.throwOnUnavailable) throw error;
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

function mapArticle(raw: unknown): GeneratedPlanningArticle | null {
  const record = asRecord(raw);
  if (!record) return null;
  const reference = String(record.reference_no || '').trim();
  const slug = String(record.slug || '').trim();
  const title = String(record.title || '').trim();
  const excerpt = String(record.excerpt || '').trim();
  const markdown = String(record.markdown || '').trim();
  if (!reference || !slug || !title || !excerpt || !markdown) return null;

  const context = asRecord(record.context) || {};
  const seo = asRecord(record.seo) || {};
  const councilValue = String(record.source_council || context.source_council || '').trim();
  const sourceCouncil = councilValue ? getCouncilConfig(councilValue)?.slug || councilValue : null;
  return {
    slug,
    reference_no: reference,
    ...(sourceCouncil ? { sourceCouncil } : {}),
    title,
    excerpt,
    markdown,
    author: String(record.author || 'City Scope'),
    location: String(record.location || (sourceCouncil ? getCouncilConfig(sourceCouncil)?.displayName : null) || 'Scotland'),
    publishedAt: String(record.published_at || record.publishedAt || new Date().toISOString()),
    updatedAt: record.updated_at || record.updatedAt ? String(record.updated_at || record.updatedAt) : undefined,
    readingTime: Number(record.reading_time || record.readingTime || 3),
    tags: asStringArray(record.tags).length ? asStringArray(record.tags) : ['Planning Applications'],
    coverLabel: String(record.cover_label || record.coverLabel || 'Worth Watching'),
    applicationPath: typeof context.applicationPath === 'string' ? context.applicationPath : buildPlanningApplicationPath(reference, sourceCouncil || undefined),
    councilLink: typeof context.councilLink === 'string' ? context.councilLink : null,
    seo: seo as GeneratedPlanningArticle['seo'],
  };
}

export function parseMarkdownToBlogSections(markdown: string): BlogSection[] {
  const lines = String(markdown || '').split(/\r?\n/);
  const sections: BlogSection[] = [];
  let paragraph: string[] = [];
  let bullets: string[] = [];

  const flushParagraph = () => {
    const text = cleanLine(paragraph.join(' '));
    if (text) sections.push({ type: 'paragraph', text });
    paragraph = [];
  };

  const flushBullets = () => {
    if (bullets.length) sections.push({ type: 'list', items: bullets });
    bullets = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushBullets();
      continue;
    }
    if (/^#\s+/.test(line)) {
      continue;
    }
    if (/^#{2,6}\s+/.test(line)) {
      flushParagraph();
      flushBullets();
      sections.push({ type: 'heading', text: cleanLine(line.replace(/^#{2,6}\s+/, '')) });
      continue;
    }
    if (/^---+$/.test(line)) {
      flushParagraph();
      flushBullets();
      sections.push({ type: 'divider' });
      continue;
    }
    if (/^>\s+/.test(line)) {
      flushParagraph();
      flushBullets();
      sections.push({ type: 'callout', text: cleanLine(line.replace(/^>\s+/, '')) });
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      bullets.push(cleanLine(line.replace(/^[-*]\s+/, '')));
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      flushParagraph();
      bullets.push(cleanLine(line.replace(/^\d+\.\s+/, '')));
      continue;
    }
    paragraph.push(line);
  }

  flushParagraph();
  flushBullets();
  return sections;
}

export function generatedArticleToBlogPost(article: GeneratedPlanningArticle): BlogPost {
  return {
    slug: article.slug,
    title: article.title,
    excerpt: article.excerpt,
    author: article.author || 'City Scope',
    location: article.location || 'Scotland',
    publishedAt: article.publishedAt,
    tags: article.tags?.length ? article.tags : ['Planning Applications'],
    readingTime: article.readingTime || 3,
    coverLabel: article.coverLabel || 'Planning Journal',
    sections: parseMarkdownToBlogSections(article.markdown),
  };
}

export async function fetchGeneratedPlanningArticles(limit = 20): Promise<GeneratedPlanningArticle[]> {
  const url = new URL(`${getApiRoot()}/articles`);
  url.searchParams.set('limit', String(limit));
  const data = await fetchJson<unknown[]>(url.toString());
  if (!Array.isArray(data)) return [];
  return data
    .map(mapArticle)
    .filter((article): article is GeneratedPlanningArticle => Boolean(article))
    .sort(
      (left, right) => new Date(right.publishedAt || right.updatedAt || 0).getTime() - new Date(left.publishedAt || left.updatedAt || 0).getTime()
    );
}

export async function fetchGeneratedPlanningBlogPosts(limit = 20): Promise<BlogPost[]> {
  const articles = await fetchGeneratedPlanningArticles(limit);
  return articles.map(generatedArticleToBlogPost);
}

export async function fetchGeneratedPlanningArticleBySlug(slug: string): Promise<GeneratedPlanningArticle | null> {
  const safeSlug = String(slug || '').trim();
  if (!safeSlug) return null;
  // A 404 is an authoritative missing article. Rate limits, timeouts and
  // upstream errors must escape to getStaticProps so ISR keeps its last good
  // page instead of caching a false redirect or notFound response.
  const data = await fetchJson<unknown>(
    `${getApiRoot()}/articles/${encodeURIComponent(safeSlug)}`,
    { throwOnUnavailable: true },
  );
  if (data === null) return null;
  const article = mapArticle(data);
  if (!article) throw new Error('Planning article API returned an invalid payload');
  return article;
}

export async function fetchPlanningArticleByReference(
  reference: unknown,
  options: FetchJsonOptions & { council?: string | null } = {}
): Promise<GeneratedPlanningArticle | null> {
  const target = normalizeRef(reference);
  if (!target) return null;
  const rawRef = String(reference || '').trim();
  const pathRef = rawRef.split('/').filter(Boolean).map(encodeURIComponent).join('/');
  if (!pathRef) return null;
  const url = new URL(`${getApiRoot()}/applications/${pathRef}/article`);
  if (options.council?.trim()) url.searchParams.set('council', options.council.trim());
  const data = await fetchJson<unknown>(url.toString(), options);
  return mapArticle(data);
}
